import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { baseUrl } from "../config";
import { useAuth } from "../AuthContext";
import { DateIRLToErache, apiDateToDate, formatEventDateLocal } from "../utils/dateConverters";
import { MaterialReactTable } from "material-react-table";
import {
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import PanoramaHorizontalIcon from "@mui/icons-material/PanoramaHorizontal";
import DetailDrawer from "./DetailDrawer";
import TimelineFilterBuilder, { makeDefaultFilters } from "./TimelineFilterBuilder";
import { Error } from "./styles";
import ChronosTimelineView from "../timeline/ChronosTimelineView";
import { CHRONOS_CANVAS_BG } from "../timeline/chronos/chronosConstants";
import { captureChronosCardToPng } from "../timeline/chronosExportPng";
import {
  normalizeTimelineViewModel,
  buildLayoutPayload,
} from "../timeline/normalizeTimelineViewModel";

const FILTER_TYPE_LABELS = {
  date_start: "Début",
  date_end: "Fin",
  category: "Catégories",
  name: "Nom",
  author: "Auteur",
  wiki_page: "Page Wikifield",
};

const OP_LABELS = {
  contains: "Contient",
  not_contains: "Ne contient pas",
  empty: "Est vide",
  before: "Avant",
  after: "Après",
  on: "Le",
  is: "Est",
  is_not: "N'est pas",
};

function summarizeRule(rule, categoryMap) {
  const type = FILTER_TYPE_LABELS[rule?.type] || rule?.type || "Règle";
  const op = OP_LABELS[rule?.op] || rule?.op || "";
  const hasValues = Array.isArray(rule?.values) && rule.values.length > 0;
  const hasValue = typeof rule?.value === "string" && rule.value.trim() !== "";

  if (hasValues) {
    const displayValues =
      rule?.type === "category"
        ? rule.values.map((v) => categoryMap[String(v)] || String(v))
        : rule.values;
    return `${type} ${op} ${displayValues.join(", ")}`;
  }
  if (hasValue) return `${type} ${op} ${rule.value}`;
  return [type, op].filter(Boolean).join(" ");
}

function FilterSummaryGroup({ node, categoryMap, depth = 0 }) {
  const conditions = Array.isArray(node?.conditions) ? node.conditions : [];
  const opLabel = node?.operator === "OR" ? "Ou" : "Et";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, ml: depth > 0 ? 1.5 : 0 }}>
      {depth > 0 && (
        <Typography variant="caption" color="text.secondary">
          Groupe ({opLabel})
        </Typography>
      )}
      {conditions.map((cond, idx) => (
        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          {idx > 0 && (
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {opLabel}
            </Typography>
          )}
          {cond?.operator ? (
            <FilterSummaryGroup node={cond} categoryMap={categoryMap} depth={depth + 1} />
          ) : (
            <Chip
              size="small"
              label={summarizeRule(cond, categoryMap)}
              variant="outlined"
              sx={{ maxWidth: "100%" }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

function validateFilterNode(node) {
  if (!node) return { ok: true, message: "" };

  // group node
  if (node.operator) {
    const op = String(node.operator).toUpperCase();
    if (op !== "AND" && op !== "OR") {
      return { ok: false, message: "Groupe de filtres invalide (Et/Ou)." };
    }
    const conds = Array.isArray(node.conditions) ? node.conditions : [];
    for (let i = 0; i < conds.length; i += 1) {
      const res = validateFilterNode(conds[i]);
      if (!res.ok) return res;
    }
    return { ok: true, message: "" };
  }

  // rule node
  const type = node.type;
  const op = String(node.op || "").toLowerCase();

  if (type === "category") {
    if (op === "empty" || op === "not_empty") return { ok: true, message: "" };
    if (op === "contains" || op === "not_contains") {
      const vals = Array.isArray(node.values) ? node.values : [];
      if (!vals.length) return { ok: false, message: "Catégories: sélection requise pour cet opérateur." };
      return { ok: true, message: "" };
    }
    return { ok: false, message: "Catégories: opérateur inconnu." };
  }

  if (type === "date_start" || type === "date_end") {
    const v = typeof node.value === "string" ? node.value.trim() : "";
    if (!v) return { ok: false, message: "Dates: une valeur est requise." };
    return { ok: true, message: "" };
  }

  if (type === "name" || type === "author" || type === "wiki_page") {
    const v = typeof node.value === "string" ? node.value.trim() : "";
    if (!v) return { ok: false, message: "Valeur requise pour ce filtre." };
    return { ok: true, message: "" };
  }

  return { ok: true, message: "" };
}

const TimelineDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pseudo } = useAuth();

  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDetail, setOpenDetail] = useState(null);

  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editFilters, setEditFilters] = useState(makeDefaultFilters());
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [categoryMap, setCategoryMap] = useState({});

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [columnFilters, setColumnFilters] = useState([]);

  const [dateMode, setDateMode] = useState("erache");
  const [savingLayout, setSavingLayout] = useState(false);
  const chartCardRef = useRef(null);
  const chronosRef = useRef(null);
  const [exportingPng, setExportingPng] = useState(false);

  const timelineViewModel = useMemo(
    () => normalizeTimelineViewModel(timeline),
    [timeline]
  );

  const laneNameById = useMemo(() => {
    const m = {};
    (timeline?.lanes || []).forEach((l) => {
      m[String(l.id)] = l.name;
    });
    return m;
  }, [timeline]);

  const fetchTimeline = useCallback(async (opts = {}) => {
    const showLoading = opts.showLoading !== false;
    try {
      if (showLoading) setLoading(true);
      setError("");
      const res = await axios.get(`${baseUrl}/timelines/${id}`, {
        withCredentials: true,
      });
      setTimeline(res.data.data);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors du chargement de la frise.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);

  const handleSaveLayout = useCallback(
    async (payload) => {
      try {
        setSavingLayout(true);
        await axios.put(`${baseUrl}/timelines/${id}/layout`, payload, {
          withCredentials: true,
        });
        await fetchTimeline({ showLoading: false });
      } catch (err) {
        console.error(err);
      } finally {
        setSavingLayout(false);
      }
    },
    [id, fetchTimeline]
  );

  const savePeriodEra = useCallback(
    async (periodId, isEra) => {
      try {
        await axios.put(
          `${baseUrl}/timelines/${id}/items/period/${periodId}/era`,
          { isEra },
          { withCredentials: true }
        );
        await fetchTimeline({ showLoading: false });
      } catch (err) {
        console.error(err);
      }
    },
    [id, fetchTimeline]
  );

  const saveItemLane = useCallback(
    async (item, newLaneId) => {
      const vm = normalizeTimelineViewModel(timeline);
      if (!vm) return;
      const itemsPayload = vm.items.map((it) =>
        it.type === item.type && String(it.id) === String(item.id)
          ? { ...it, laneId: newLaneId }
          : it
      );
      await handleSaveLayout(buildLayoutPayload(vm.lanes, itemsPayload));
    },
    [timeline, handleSaveLayout]
  );

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  useEffect(() => {
    axios
      .get(`${baseUrl}/event-categories`, { withCredentials: true })
      .then((res) => {
        const next = {};
        (res.data || []).forEach((cat) => {
          next[String(cat.id)] = cat.name;
        });
        setCategoryMap(next);
      })
      .catch(() => {});
  }, []);

  const goToEditRoute = () => {
    navigate(`/timelines/${id}/edit`);
  };

  const handleExportPng = useCallback(async () => {
    const el = chartCardRef.current;
    if (!el || !timeline) return;
    try {
      setExportingPng(true);
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      if (el.offsetWidth < 4 || el.offsetHeight < 4) {
        console.warn("Export PNG: zone du graphique trop petite.");
        return;
      }
      const dataUrl = await captureChronosCardToPng(el, {
        backgroundColor: CHRONOS_CANVAS_BG,
        skipFonts: true,
      });
      const base = (timeline.name || "frise")
        .replace(/[/\\?%*:|"<>]/g, "-")
        .slice(0, 120);
      const link = document.createElement("a");
      link.download = `${base}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error(e);
    } finally {
      setExportingPng(false);
    }
  }, [timeline]);

  const handleExportWidePng = useCallback(async () => {
    if (!timeline) return;
    const api = chronosRef.current;
    if (!api?.exportWideFullSpanPng) return;
    try {
      setExportingPng(true);
      const dataUrl = await api.exportWideFullSpanPng();
      if (!dataUrl) return;
      const base = `${(timeline.name || "frise").replace(/[/\\?%*:|"<>]/g, "-").slice(0, 100)}-pleine-frise`;
      const link = document.createElement("a");
      link.download = `${base}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      window.alert(e?.message || String(e));
      console.error(e);
    } finally {
      setExportingPng(false);
    }
  }, [timeline]);

  const leaveEditRoute = () => {
    navigate(`/timelines/${id}`);
  };

  const saveEdit = async () => {
    try {
      setSaving(true);
      setSaveMessage("");

      const validation = validateFilterNode(editFilters);
      if (!validation.ok) {
        setSaveMessage(validation.message);
        setSaving(false);
        return;
      }

      await axios.put(
        `${baseUrl}/timelines/${id}`,
        {
          name: editName,
          description: editDescription,
          filters: editFilters,
          includeEvents: [],
          excludeEvents: [],
          includePeriods: [],
          excludePeriods: [],
        },
        { withCredentials: true }
      );
      setSaveMessage("Enregistré !");
      leaveEditRoute();
      fetchTimeline();
    } catch (err) {
      setSaveMessage(err.response?.data?.error || "Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const isOwner = timeline && pseudo && timeline.authorPseudo === pseudo;
  const editModeRoute = isOwner && location.pathname.endsWith("/edit");

  useEffect(() => {
    if (!timeline || !editModeRoute) return;
    setEditName(timeline.name);
    setEditDescription(timeline.description || "");
    try {
      const f =
        typeof timeline.filters === "string" ? JSON.parse(timeline.filters) : timeline.filters;
      setEditFilters(f && f.operator ? f : makeDefaultFilters());
    } catch {
      setEditFilters(makeDefaultFilters());
    }
    setSaveMessage("");
  }, [timeline, editModeRoute]);
  const items = timeline?.items || [];
  let displayFilters = makeDefaultFilters();
  try {
    const parsed = typeof timeline?.filters === "string" ? JSON.parse(timeline.filters) : timeline?.filters;
    if (parsed && parsed.operator && Array.isArray(parsed.conditions)) {
      displayFilters = parsed;
    }
  } catch {
    // keep default fallback
  }

  const columns = useMemo(
    () => [
    {
      accessorKey: "laneId",
      header: "Voie",
      size: 160,
      grow: 0,
      Cell: ({ row }) => {
        const it = row.original;
        if (editModeRoute && isOwner && timeline?.lanes?.length) {
          const lanesSorted = [...timeline.lanes].sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
          );
          return (
            <FormControl size="small" sx={{ minWidth: 120 }} onClick={(e) => e.stopPropagation()}>
              <Select
                value={String(it.laneId)}
                onChange={(e) => saveItemLane(it, e.target.value)}
              >
                {lanesSorted.map((l) => (
                  <MenuItem key={String(l.id)} value={String(l.id)}>
                    {l.name || "—"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return laneNameById[String(it.laneId)] || "—";
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: "isEra",
      accessorKey: "isEra",
      header: "Ère",
      size: 72,
      grow: 0,
      Cell: ({ row }) => {
        const it = row.original;
        if (it.type !== "period") return "—";
        if (editModeRoute && isOwner) {
          return (
            <Checkbox
              checked={!!it.isEra}
              onChange={(e) => {
                e.stopPropagation();
                savePeriodEra(it.id, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
          );
        }
        return it.isEra ? "Oui" : "Non";
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 80,
      grow: 0,
      Cell: ({ row }) =>
        row.original.type === "event" ? "Évènement" : "Période",
      enableColumnFilter: true,
      filterVariant: "select",
      filterSelectOptions: [
        { label: "Évènement", value: "event" },
        { label: "Période", value: "period" },
      ],
    },
    {
      accessorKey: "name",
      header: "Nom",
      size: 420,
      minSize: 160,
      maxSize: 960,
      grow: 1,
      enableColumnFilter: true,
      filterVariant: "text",
      muiTableBodyCellProps: {
        sx: { whiteSpace: "nowrap" },
      },
    },
    {
      id: "startErache",
      accessorKey: "startDate",
      header: "Début (Erache)",
      size: 240,
      minSize: 200,
      grow: 0,
      Cell: ({ row }) =>
        row.original.startDate
          ? DateIRLToErache(apiDateToDate(row.original.startDate))
          : "—",
      enableColumnFilter: false,
      enableSorting: false,
      muiTableBodyCellProps: {
        sx: { whiteSpace: "nowrap" },
      },
    },
    {
      id: "startIrl",
      accessorKey: "startDate",
      header: "Début (IRL)",
      size: 148,
      grow: 0,
      Cell: ({ row }) =>
        row.original.startDate
          ? formatEventDateLocal(row.original.startDate)
          : "—",
      enableColumnFilter: false,
    },
    {
      id: "endErache",
      accessorKey: "endDate",
      header: "Fin (Erache)",
      size: 240,
      minSize: 200,
      grow: 0,
      Cell: ({ row }) => {
        if (!row.original.endDate) {
          return row.original.type === "period" ? "En cours" : "—";
        }
        return DateIRLToErache(apiDateToDate(row.original.endDate));
      },
      enableColumnFilter: false,
      enableSorting: false,
      muiTableBodyCellProps: {
        sx: { whiteSpace: "nowrap" },
      },
    },
    {
      id: "endIrl",
      accessorKey: "endDate",
      header: "Fin (IRL)",
      size: 148,
      grow: 0,
      Cell: ({ row }) => {
        if (!row.original.endDate) {
          return row.original.type === "period" ? "En cours" : "—";
        }
        return formatEventDateLocal(row.original.endDate);
      },
      enableColumnFilter: false,
    },
    ],
    [
      editModeRoute,
      isOwner,
      timeline,
      laneNameById,
      saveItemLane,
      savePeriodEra,
    ]
  );

  if (loading) {
    return (
      <PageContainer>
        <Typography>Chargement…</Typography>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Error>{error}</Error>
      </PageContainer>
    );
  }

  if (!timeline) return null;

  return (
    <PageContainer>
      {!editModeRoute ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, mb: 1, flexWrap: "wrap" }}>
            <Title>{timeline.name}</Title>
            <Typography variant="body2" color="text.secondary">
              par {timeline.authorPseudo}
            </Typography>
            {isOwner && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={goToEditRoute}
              >
                Éditer
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={handleExportPng}
              disabled={exportingPng || !timelineViewModel}
              title="Image de la fenêtre temporelle visible (zoom / déplacement actuels)"
            >
              {exportingPng ? "Export…" : "PNG (vue)"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PanoramaHorizontalIcon />}
              onClick={handleExportWidePng}
              disabled={exportingPng || !timelineViewModel}
              title="Toute la frise (min→max données), même échelle que la vue actuelle. Fichier très large si le zoom est serré. Limite 32000 px de large."
            >
              PNG (toute la frise)
            </Button>
          </Box>
          {timeline.description && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              {timeline.description}
            </Typography>
          )}
          <Box
            sx={{
              mb: 2,
              p: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "background.paper",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Filtrage
            </Typography>
            <FilterSummaryGroup node={displayFilters} categoryMap={categoryMap} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Title>Édition — {timeline.name}</Title>
            </Box>
            <Button size="small" variant="outlined" onClick={leaveEditRoute}>
              Terminer
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={handleExportPng}
              disabled={exportingPng || !timelineViewModel}
              title="Image de la fenêtre temporelle visible"
            >
              {exportingPng ? "Export…" : "PNG (vue)"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PanoramaHorizontalIcon />}
              onClick={handleExportWidePng}
              disabled={exportingPng || !timelineViewModel}
              title="Toute la frise à l'échelle actuelle. Limite 32000 px."
            >
              PNG (toute la frise)
            </Button>
          </Box>
          <TextField
            label="Nom de la frise"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
            sx={{ mb: 2 }}
          />
          <TimelineFilterBuilder filters={editFilters} onChange={setEditFilters} />
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={saveEdit} disabled={saving}>
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
            <Button variant="outlined" onClick={leaveEditRoute} disabled={saving}>
              Annuler
            </Button>
            {saveMessage && (
              <Typography variant="body2" sx={{ alignSelf: "center" }}>
                {saveMessage}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {timelineViewModel && (
        <ChronosTimelineView
          ref={chronosRef}
          viewModel={timelineViewModel}
          editMode={!!editModeRoute}
          isOwner={!!isOwner}
          dateMode={dateMode}
          onDateModeChange={setDateMode}
          onItemClick={setOpenDetail}
          onSaveLayout={handleSaveLayout}
          savingLayout={savingLayout}
          chartCardRef={chartCardRef}
        />
      )}

      <TableWrapper>
        <MaterialReactTable
          columns={columns}
          data={items}
          layoutMode="grid"
          muiTablePaperProps={{
            elevation: 0,
              sx: { width: "100%", maxWidth: "100%", backgroundColor: "var(--bg)" },
          }}
          muiTableContainerProps={{
              sx: {
                overflowX: "auto",
                maxWidth: "100%",
                backgroundColor: "var(--bg)",
              },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () =>
              setOpenDetail({
                type: row.original.type,
                id: row.original.id,
              }),
            sx: {
              cursor: "pointer",
              backgroundColor: "var(--bg)",
              "&:hover": { backgroundColor: "var(--bg)" },
            },
          })}
          enableColumnOrdering={false}
          enableColumnResizing
          enableSorting
          enableColumnActions={false}
          enablePagination
          enableFilters
          enableTopToolbar={false}
          muiBottomToolbarProps={{
            sx: {
              backgroundColor: "var(--bg)",
              color: "var(--text-muted)",
              "& .MuiButtonBase-root": { color: "inherit" },
            },
          }}
          state={{ pagination, columnFilters }}
          onPaginationChange={setPagination}
          onColumnFiltersChange={setColumnFilters}
          initialState={{ density: "compact" }}
          muiTableBodyCellProps={{
            sx: { verticalAlign: "middle", backgroundColor: "var(--bg)" },
          }}
          muiTableHeadCellProps={{
            sx: { verticalAlign: "middle", backgroundColor: "var(--bg)" },
          }}
          muiTableHeadRowProps={{
            sx: { backgroundColor: "var(--bg)" },
          }}
        />
      </TableWrapper>

      <DetailDrawer
        open={openDetail}
        onClose={() => setOpenDetail(null)}
        onOpenDetail={setOpenDetail}
        onEventDeleted={() => fetchTimeline({ showLoading: false })}
        onPeriodDeleted={() => fetchTimeline({ showLoading: false })}
        DateIRLToErache={DateIRLToErache}
      />
    </PageContainer>
  );
};

export default TimelineDetail;

/* Standalone layout: full viewport width even when a parent flex box is content-sized on cold load. */
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  box-sizing: border-box;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  min-width: 0;
  flex: 1 1 auto;
  /* Horizontal inset: comfortable margins on narrow and wide viewports */
  padding: 20px clamp(16px, 4vw, 40px) 28px;
  overflow-x: auto;
`;

const TableWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  box-sizing: border-box;
`;

const Title = styled.h1`
  margin-bottom: 0px;
`;
